// Help modal (ui-qol §9.3): the "?" button in the menu's left column. Same chrome as Settings;
// title "How to play", a controls table (mouse, Space, W, Q, Esc, wheel, and the cash-out key in
// money arenas), the touch line, and "Show the controls hint again" (clears the onboarding flags so
// the first-play hint shows on the next spawn).
//
// `helpRows` is DOM-free (unit-tested). Consumer: app.ts.

import type { UiClickSource } from '../../events/bus.js';
import { h } from '../dom.js';
import { t } from '../i18n/t.js';
import { type ModalHandle, openModal } from './modal.js';

export interface HelpRow {
  readonly key: string;
  readonly action: string;
}

/** The controls table (`cashOutKey` adds the money row). */
export function helpRows(cashOutKey: string | null = null): HelpRow[] {
  const rows: HelpRow[] = [
    { key: t('help.mouse'), action: t('help.mouse.do') },
    { key: t('menu.key.space'), action: t('help.space.do') },
    { key: t('menu.key.w'), action: t('help.w.do') },
    { key: 'Q', action: t('help.q.do') },
    { key: t('help.esc'), action: t('help.esc.do') },
    { key: t('help.wheel'), action: t('help.wheel.do') },
  ];
  if (cashOutKey) rows.push({ key: cashOutKey.toUpperCase(), action: t('help.cashOut.do') });
  return rows;
}

export interface HelpDeps {
  onShowHintAgain(): void;
  /** The money arenas' cash-out key, or null when money arenas are off. */
  cashOutKey(): string | null;
  playClick?(source: UiClickSource): void;
  onClose?(): void;
}

export function openHelp(deps: HelpDeps): ModalHandle {
  const done = h(
    'p',
    { class: 'hint-again-done', role: 'status', hidden: true },
    t('help.againDone'),
  );
  const content = h(
    'div',
    { class: 'help' },
    h('h2', { class: 'help-title' }, t('help.title')),
    h(
      'table',
      { class: 'help-table' },
      h(
        'tbody',
        null,
        helpRows(deps.cashOutKey()).map((r) =>
          h(
            'tr',
            null,
            h('th', null, h('span', { class: 'help-key' }, r.key)),
            h('td', null, r.action),
          ),
        ),
      ),
    ),
    h('p', { class: 'help-touch' }, t('help.touch')),
    h(
      'button',
      {
        type: 'button',
        class: 'btn-help-again',
        onClick: () => {
          deps.playClick?.('help');
          deps.onShowHintAgain();
          done.hidden = false;
        },
      },
      t('help.again'),
    ),
    done,
  );
  return openModal(content, {
    label: t('help.title'),
    className: 'modal-settings modal-help',
    onCloseClick: () => deps.playClick?.('close'),
    ...(deps.onClose ? { onClose: deps.onClose } : {}),
  });
}
